/**
 * MUI Floorplan component that replaces the styled-components Floorplan component
 * 
 * This component provides a Material UI equivalent of the original Floorplan component
 * with similar props and functionality.
 */
import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox, getCurrentView } from "reduxModules/ducks/ui";
import {
  FloorplanProps,
  FloorplanLine,
  FloorplanPathProps,
  FloorplanBackProps,
  SVGPathProps
} from "types/components/elements";
import { RootState } from "types/redux/state";
import { distanceToString } from "utilities/format";

/**
 * MUI Floorplan component
 * 
 * @returns {JSX.Element} MUI Floorplan component
 */
const MuiFloorplan: React.FC<FloorplanProps> = (props) => {
  const containerOrArea = props.children;
  const theme = useTheme();

  const {bbox, scale, zoom} = useSelector(getDrawingBox);
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const currentView = useSelector(getCurrentView);

  let lines: FloorplanLine[] = [];
  let isArea: boolean;
  if (containerOrArea) {
    // Check if this is an area (has an id) or the main container
    isArea = containerOrArea.id === activeTab;
    const isContainer = !containerOrArea.id; // Container doesn't have an id
    
    // Use container dimensions if it's the main container, otherwise use area dimensions
    const dimensions = isContainer ? containerOrArea.dimensions : containerOrArea.dimensions;
    const { length, width } = dimensions || {};
    const { left = 0, top = 0 } = containerOrArea?.location || {};
    const { top: tw = true, right: rw = true, bottom: bw = true, left: lw = true } = containerOrArea?.surroundingWalls || {};
    const { x0: OriginX, y0: OriginY } = bbox;

    // Enhanced validation for line generation - ensure both length and width are valid numbers
    const validLength = isFinite(length) && length > 0;
    const validWidth = isFinite(width) && width > 0;

    if (validLength && validWidth) {
      lines = [...Array(2)].flatMap((_, index) => {
        const odd = index % 2;
        const sign = odd ? -1 : 1;
        const x0 = OriginX + (!zoom ? left : 0 + odd * length) * scale;
        const y0 = OriginY + (!zoom ? top : 0 + odd * width) * scale;
        const x1 = x0 + sign * length * scale; 
        const y1 = y0 + sign * width * scale;
        return [
          { 
            d: `m${x0} ${y0}H${x1}`, 
            dash: odd ? bw : tw, 
            bg: odd ? bbox.y1 - y1 - y0 <= 1 : bbox.y0 - y0 <= 1 
          },
          { 
            d: `m${x0} ${y0}V${y1}`, 
            dash: odd ? rw : lw, 
            bg: odd ? bbox.x1 -x1 - x0 <= 1: bbox.x0 - x0 <= 1 
          }
        ];
      });
    }
  }

  // Safely get condition results with fallback values
  const visible = useSelector((state: RootState) => {
    try {
      return getConditionResult(state, props.show ? {...props.show, area: containerOrArea?.id || 'container'} : { parameter: [], check: () => true, area: containerOrArea?.id || 'container' });
    } catch (error) {
      console.warn('MuiFloorplan: Error evaluating show condition:', error);
      return true; // Default to visible on error
    }
  });
  
  const active = useSelector((state: RootState) => {
    try {
      return getConditionResult(state, props.active ? {...props.active, area: containerOrArea?.id || 'container'} : { parameter: [], check: () => false, area: containerOrArea?.id || 'container' });
    } catch (error) {
      console.warn('MuiFloorplan: Error evaluating active condition:', error);
      return false; // Default to inactive on error
    }
  });

  // Create rectangular room path with 90-degree corners
  const createRectangularRoomPath = () => {
    if (!containerOrArea?.dimensions) {
      return '';
    }
    
    const { length, width } = containerOrArea.dimensions;
    
    // Validate dimensions are positive finite numbers
    if (!isFinite(length) || !isFinite(width) || length <= 0 || width <= 0) {
      return '';
    }
    
    const { left = 0, top = 0 } = containerOrArea?.location || {};
    const { x0: OriginX, y0: OriginY } = bbox;
    
    if (!isFinite(OriginX) || !isFinite(OriginY) || !isFinite(scale)) {
      return '';
    }
    
    const x = OriginX + (!zoom ? left : 0) * scale;
    const y = OriginY + (!zoom ? top : 0) * scale;
    const w = length * scale;
    const h = width * scale;
    
    // Additional validation for calculated values
    if (!isFinite(x) || !isFinite(y) || !isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) {
      return '';
    }
    
    // Create rectangular path with 90-degree corners
    return `M ${x} ${y} 
            L ${x + w} ${y} 
            L ${x + w} ${y + h}
            L ${x} ${y + h}
            Z`;
  };

  const rectangularPath = createRectangularRoomPath();

  // Get units from state
  const isMeter = useSelector((state: any) => state.ui?.units === 'meters');
  
  // Format dimension for display using the same utility as LineDimension
  const formatDimension = (value: number) => {
    return distanceToString(value, isMeter);
  };

  // Always render the group to ensure consistent DOM structure
  return (
    visible && (
      <FloorplanGroup id={`${containerOrArea.id || 'container'}-floorplan`}>
        {/* Primary: Render rectangular room when valid dimensions are available */}
        {rectangularPath && active && (
          <FloorplanBack 
            key="rectangular-room-back"
            d={rectangularPath}
            bg={false}
          />
        )}
        {rectangularPath && (
          <>
            <FloorplanPath 
              key="rectangular-room"
              d={rectangularPath}
              dash={false}
              bg={false}
              active={active} 
              isArea={isArea}
              currentView={currentView}
            />
            {/* Add dimension labels */}
            {containerOrArea?.dimensions && (
              <>
                {/* Width label (bottom) */}
                <text
                  x={bbox.x0 + (bbox.x1 - bbox.x0) / 2}
                  y={bbox.y1 + 30}
                  textAnchor="middle"
                  fontSize="16"
                  fontWeight="500"
                  fill={theme.palette.text.primary}
                >
                  {formatDimension(containerOrArea.dimensions.length)}
                </text>
                {/* Height label (right) */}
                <text
                  x={bbox.x1 + 30}
                  y={bbox.y0 + (bbox.y1 - bbox.y0) / 2}
                  textAnchor="middle"
                  fontSize="16"
                  fontWeight="500"
                  fill={theme.palette.text.primary}
                  transform={`rotate(90 ${bbox.x1 + 30} ${bbox.y0 + (bbox.y1 - bbox.y0) / 2})`}
                >
                  {formatDimension(containerOrArea.dimensions.width)}
                </text>
              </>
            )}
          </>
        )}
        
        {/* Fallback: Original line-based rendering when rectangular path is not available */}
        {!rectangularPath && lines.length > 0 && lines.map((line, index) => (
          <FloorplanPath
            key={`line-${index}`}
            d={line.d}
            dash={line.dash}
            bg={line.bg}
            active={active}
            isArea={isArea}
            currentView={currentView}
          />
        ))}
        
        {/* Emergency fallback: Show a basic rectangle if nothing else renders */}
        {!rectangularPath && lines.length === 0 && containerOrArea?.dimensions && (
          <FloorplanPath
            key="emergency-fallback"
            d={`M ${bbox.x0 + 10} ${bbox.y0 + 10} L ${bbox.x0 + 200} ${bbox.y0 + 10} L ${bbox.x0 + 200} ${bbox.y0 + 150} L ${bbox.x0 + 10} ${bbox.y0 + 150} Z`}
            dash={true}
            bg={false}
            active={active}
            isArea={isArea}
            currentView={currentView}
          />
        )}
      </FloorplanGroup>
    )
  );
};

// Styled components using MUI styled API
const FloorplanGroup = styled('g')`
  fill: none;
  stroke-width: 2;
  shape-rendering: crispedges;
`;

const FloorplanBack = styled('path')<FloorplanBackProps & SVGPathProps>(({ bg }) => ({
  stroke: '#fff',
  opacity: bg ? '.5' : '1',
}));

const FloorplanPath = styled('path')<FloorplanPathProps & SVGPathProps>(({ active, dash, bg, isArea, currentView }) => ({
  stroke: currentView === 'floor_plan' || currentView === 'floorplan' ? '#007bff' : '#6b7280', // Blue when floorplan is selected, grey otherwise
  strokeDasharray: 0, // Solid line (no dashes)
  strokeWidth: 2,
  fill: 'none',
  opacity: bg && isArea ? '1' : '1',
}));

export default MuiFloorplan;
